import { Button } from "@/components/ui/button/button";
import Typography from "@/components/ui/typography";
import { isSetupComplete } from "@/utils/setup";
import { Link, useLoaderData, useNavigate } from "@tanstack/react-router";
import {
	ArrowRight,
	Building2,
	CreditCard,
	LogIn,
	LogOut,
	XIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

const STEPS = [
	{
		id: "business-details",
		title: "Add business details",
		description: "Add your business profile before linking payment methods",
		href: "/main/setting/business-details/create",
		icon: Building2,
		isComplete: (ctx: OnboardingContext) => ctx.hasBusinessDetails,
	},
	{
		id: "credit-card",
		title: "Add a credit card",
		description: "Link your credit card to convert credit to cash",
		href: "/main/card/new",
		icon: CreditCard,
		isComplete: (ctx: OnboardingContext) => ctx.hasCreditCard,
	},
	{
		id: "bank-account",
		title: "Add a bank account",
		description: "Connect your bank to receive transfers",
		href: "/main/setting/linked-accounts/new",
		icon: Building2,
		isComplete: (ctx: OnboardingContext) => ctx.hasBankAccount,
	},
	{
		id: "transfer-in",
		title: "Transfer in",
		description: "Convert credit to cash in your account",
		href: "/main/deposit",
		icon: LogIn,
		isComplete: (ctx: OnboardingContext) => ctx.hasTransferIn,
	},
	{
		id: "transfer-out",
		title: "Transfer out",
		description: "Send money to your linked bank account",
		href: "/main/withdraw",
		icon: LogOut,
		isComplete: (ctx: OnboardingContext) => ctx.hasTransferOut,
	},
] as const;

type OnboardingContext = {
	hasBusinessDetails: boolean;
	hasCreditCard: boolean;
	hasBankAccount: boolean;
	hasTransferIn: boolean;
	hasTransferOut: boolean;
};

const OnboardingPage = () => {
	const navigate = useNavigate();
	const { user, creditCards, bankAccounts, transactionsData } = useLoaderData({
		from: "/main",
	});

	const creditCardsList = creditCards;
	const bankAccountsList = bankAccounts;
	const transactions = transactionsData.data ?? [];

	const hasBusinessDetails = user.guest_business_details.length > 0;
	const hasCreditCard = creditCardsList.length > 0;
	const hasBankAccount = bankAccountsList.length > 0;
	const hasTransferIn = transactions.some(
		(t) => t.transaction_type_name === "Deposit",
	);
	const hasTransferOut = transactions.some(
		(t) => t.transaction_type_name === "Withdrawal",
	);

	const context: OnboardingContext = {
		hasBusinessDetails,
		hasCreditCard,
		hasBankAccount,
		hasTransferIn,
		hasTransferOut,
	};

	const completedCount = STEPS.filter((s) => s.isComplete(context)).length;
	const progressPercent = (completedCount / STEPS.length) * 100;

	const setupComplete = isSetupComplete(
		creditCardsList,
		bankAccountsList,
		transactions,
	);

	return (
		<div className="flex flex-col min-h-full px-6 py-6">
			<div className="flex items-center justify-end mb-6">
				<Button
					variant="ghost"
					size="compact"
					className="p-0"
					onClick={() => navigate({ to: "/main/home" })}
				>
					<XIcon className="size-5 text-textDark" />
				</Button>
			</div>

			<div className="flex flex-col gap-2 mb-8">
				<Typography variant="h1" color="primary">
					Get set up with your account
				</Typography>
				<Typography variant="p" color="primary">
					Follow these steps to complete your account setup and start using your
					balance.
				</Typography>
			</div>

			<div className="mb-8">
				<div className="flex items-center justify-between mb-2">
					<Typography variant="p" color="primary">
						{completedCount} of {STEPS.length} completed
					</Typography>
					<Typography variant="h6" color="primary">
						{Math.round(progressPercent)}%
					</Typography>
				</div>
				<div className="h-2 w-full overflow-hidden rounded-full bg-primary-100">
					<div
						className="h-full rounded-full bg-primary-600 transition-all duration-300"
						style={{ width: `${progressPercent}%` }}
					/>
				</div>
			</div>

			<div className="flex flex-col gap-4">
				{STEPS.map((step, index) => {
					const isComplete = step.isComplete(context);
					const allPreviousComplete = STEPS.slice(0, index).every((s) =>
						s.isComplete(context),
					);
					const isClickable = isComplete || allPreviousComplete;
					const Icon = step.icon;

					const stepContent = (
						<>
							<div
								key="icon"
								className={cn(
									"flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center",
									isComplete ? "bg-primary-100" : "bg-primary-50",
								)}
							>
								<Icon
									className={cn(
										"size-6 text-primary-600",
										isComplete && "opacity-90",
										!isClickable && "opacity-50",
									)}
								/>
							</div>
							<div key="content" className="flex-1 min-w-0">
								<div className="flex items-center justify-between gap-2">
									<Typography variant="h5" color="primary">
										{index + 1}. {step.title}
									</Typography>
									{isComplete ? (
										<span className="inline-flex rounded-full bg-primary-100 px-2 py-0.5">
											<Typography variant="h6" color="primary">
												Done
											</Typography>
										</span>
									) : isClickable ? (
										<ArrowRight className="size-5 flex-shrink-0 text-primary-600" />
									) : (
										<span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5">
											<Typography variant="h6" color="primary-light">
												Complete previous step
											</Typography>
										</span>
									)}
								</div>
								<Typography variant="p" color="primary">
									{step.description}
								</Typography>
							</div>
						</>
					);

					return isClickable ? (
						<Link
							key={step.id}
							to={step.href}
							className={cn(
								"flex items-start gap-4 p-4 rounded-2xl border transition-colors",
								isComplete
									? "bg-primary-50/50 border-primary-200"
									: "bg-white border-border hover:border-primary-300 hover:bg-primary-50/30",
							)}
						>
							{stepContent}
						</Link>
					) : (
						<div
							key={step.id}
							className={cn(
								"flex items-start gap-4 p-4 rounded-2xl border transition-colors cursor-not-allowed",
								"bg-gray-50/50 border-border opacity-75",
							)}
						>
							{stepContent}
						</div>
					);
				})}
			</div>

			<div className="mt-auto pt-8">
				<Button
					className="w-full"
					onClick={() => navigate({ to: "/main/home" })}
				>
					{setupComplete ? "You're all set!" : "Back to Home"}
				</Button>
			</div>
		</div>
	);
};

export default OnboardingPage;
